import { GameManager } from '../game/GameManager';

export enum CursorState {
  pointer = 'pointer',
  default = 'default',
  move = 'move',
  attack = 'attack',
  attackTarget = 'attackTarget'
}

export const cursorConfigMap = {
  [CursorState.default]: {
    src: 'url(https://staticcontent.knowledgehook.com/content/khg/ui/cursors/default.png), default',
    priority: 1
  },
  [CursorState.move]: {
    src: 'url(https://staticcontent.knowledgehook.com/content/khg/ui/cursors/move.png), default',
    priority: 2
  },
  [CursorState.attack]: {
    src: 'url(https://staticcontent.knowledgehook.com/content/khg/ui/cursors/attack.png), pointer',
    priority: 4
  },
  [CursorState.attackTarget]: {
    src: 'url(https://staticcontent.knowledgehook.com/content/khg/ui/cursors/attack-target.png), pointer',
    priority: 5
  },
  [CursorState.pointer]: {
    src: 'url(https://staticcontent.knowledgehook.com/content/khg/ui/cursors/pointer.png), pointer',
    priority: 6
  }
};

// ? - event.buttons => 0: None, 1: Primary, 2: Secondary; See: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
export enum MouseButtonsActive {
  None = 0,
  Primary = 1,
  Secondary = 2
}

/**
 * Manages the cursor state and provides methods to change the cursor.
 */
export class CursorManager {
  private static _cursorDataURIs: Map<CursorState, string> = new Map();
  public static state: CursorState = CursorState.default;

  /**
   * Preloads all cursor images and converts them to data URIs
   */
  public static async preloadCursors(): Promise<void> {
    const loadPromises = Object.entries(cursorConfigMap).map(async ([state, config]) => {
      // Extract URL from the src string
      const url = config.src.match(/url\((.*?)\)/)?.[1];
      if (!url) { return undefined; }

      const cleanUrl = url.replace(/['"]/g, '');

      // Fetch the image and convert to data URI
      const response = await fetch(cleanUrl);
      const blob = await response.blob();

      return new Promise<void>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUri = reader.result as string;
          const fallback = config.src.split(',')[1].trim();
          CursorManager._cursorDataURIs.set(state as CursorState, `url(${dataUri}), ${fallback}`);
          resolve();
        };
        reader.readAsDataURL(blob);
      });
    });

    await Promise.all(loadPromises);
  }

  /**
   * Enters the specified cursor state if it has higher priority than the current state.
   * @param state The cursor state to enter.
   */
  public static enterState(state: CursorState) {
    if (CursorManager.state === state) {
      return;
    }

    if (cursorConfigMap[state].priority < cursorConfigMap[CursorManager.state].priority) {
      return;
    }
    CursorManager.changeCursor(state);
  }

  /**
   * Exits the specified cursor state if it has higher priority than the default state.
   * @param state The cursor state to exit.
   */
  public static exitState(state: CursorState) {
    if (CursorManager.state === CursorState.default) {
      return;
    }

    if (cursorConfigMap[state].priority < cursorConfigMap[CursorManager.state].priority) {
      return;
    }
    CursorManager.changeCursor(CursorState.default);
  }

  /**
   * Changes the cursor state to the specified state.
   * @param state The new cursor state.
   */
  public static changeCursor(state: CursorState) {
    CursorManager.state = state;
    if (GameManager.app.canvas.style) {
      // Use data URI if available, otherwise fall back to original URL
      const cursorValue = CursorManager._cursorDataURIs.get(state) ?? cursorConfigMap[state].src;
      GameManager.app.canvas.style.cursor = cursorValue;
    }
  }
}
